import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, split } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createUploadLink } from 'apollo-upload-client';
import { extractFiles } from 'extract-files';

import { showErrorToUser } from '@pletly/ui';
import { CustomHeader, ApiFeatureHeaderValue } from '@pletly/shared';
import { getAccessToken, createRestartableWebsocketClient } from '../helpers';
import { API_URL } from '../config';
import { typePolicies } from './typePolicies';
import { shouldBatchRequests } from './batchBlacklist';

const LINK_OPTIONS = {
	uri: API_URL,
	headers: {
		'Apollo-Require-Preflight': 'true',
		[CustomHeader.ApiFeatures]: ApiFeatureHeaderValue.NewMediaUrl,
	},
	credentials: 'include',
} as const;

const batchHttpLink = ApolloLink.split(
	shouldBatchRequests,
	new BatchHttpLink(LINK_OPTIONS),
	new HttpLink(LINK_OPTIONS),
);

const httpLink = ApolloLink.split(
	(operation) => extractFiles(operation).files.size > 0,
	createUploadLink(LINK_OPTIONS) as unknown as ApolloLink,
	batchHttpLink,
);

if (__DEV__) {
	loadDevMessages();
	loadErrorMessages();
}

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors }) => {
	if (graphQLErrors) {
		graphQLErrors.forEach(({ extensions, message, locations, path }) => {
			if (extensions?.code === '404') {
				return showErrorToUser({ message }, { title: 'Not found' });
			}

			console.error(
				`[GraphQL error]: Message: ${message}, Location: ${locations?.toString() || ''}, Path: ${
					path?.toString() || ''
				}`,
			);
		});
	}
});

const authLink = setContext(
	async (request, { headers }: { headers: Record<string, string | number> }) => {
		const token = await getAccessToken(client, request);

		return {
			headers: {
				...headers,
				authorization: token ? `Bearer ${token}` : '',
			},
		};
	},
);

const webSocketUri = API_URL.replace(/^http?/, 'ws');
const webSocketClient = createRestartableWebsocketClient({
	url: webSocketUri,
	keepAlive: 30000,
	connectionParams: async () => {
		const authToken = await getAccessToken(client);

		return {
			authToken,
			[CustomHeader.ApiFeatures]: ApiFeatureHeaderValue.NewMediaUrl,
		};
	},
	shouldRetry: () => true,
});

export const restartWebSocketClient = webSocketClient.restart;

const wsLink = new GraphQLWsLink(webSocketClient);

const splitLink = split(
	({ query }) => {
		const definition = getMainDefinition(query);

		return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
	},
	wsLink,
	authLink.concat(httpLink),
);

export const client = new ApolloClient({
	link: from([errorLink, splitLink]),
	cache: new InMemoryCache({
		typePolicies,
	}),
});

export const unauthenticatedClient = new ApolloClient({
	link: from([errorLink, httpLink]),
	cache: new InMemoryCache(),
});
